import { useEffect } from "react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { Instagram, ArrowUpRight, MessageCircle, Check } from "lucide-react";

const INSTAGRAM_URL = "https://www.instagram.com/pietervanherpen";

function WhatsAppIcon({ className = "h-5 w-5" }) {
    return (
        <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.611-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479c0 1.462 1.065 2.875 1.213 3.074.149.198 2.095 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zM12.04 21.785h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.981.999-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.889-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.885-9.887 9.885zm8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
    );
}

export default function ThankYou() {
    useEffect(() => {
        window.scrollTo(0, 0);
        // Fire conversion event for ad platforms (Meta Pixel, Google Ads) when they're added
        if (typeof window !== "undefined" && window.dataLayer) {
            window.dataLayer.push({ event: "lead_submitted" });
        }
    }, []);

    return (
        <div data-testid="thank-you-page" className="min-h-screen bg-cream text-ink flex flex-col">
            <SEO
                path="/thank-you"
                title="You're in — Verde Investing"
                description="Welcome to the Verde Investing WhatsApp broadcast. The next deal memo will land directly on your phone."
                noindex
            />
            <header className="bg-forest-deep border-b border-cream/10">
                <div className="max-w-7xl mx-auto px-6 md:px-12 py-3 flex items-center justify-between">
                    <Link to="/" className="flex items-center" data-testid="thank-you-home">
                        <img
                            src="/verde2050-logo-silver.png"
                            alt="Verde 2050"
                            className="h-[18px] md:h-[22px] w-auto object-contain"
                           
                        />
                    </Link>
                    <Link to="/" className="mono-label text-cream/70 hover:text-cream transition-colors">
                        ← Back to site
                    </Link>
                </div>
            </header>

            <main className="flex-1 flex items-center">
                <div className="max-w-5xl mx-auto px-6 md:px-12 py-20 md:py-28 w-full grid md:grid-cols-12 gap-12 md:gap-16 items-center">
                    {/* Left: confirmation */}
                    <div className="md:col-span-7 stagger">
                        <div className="flex items-center gap-3 mb-8">
                            <div className="w-10 h-10 rounded-full bg-forest flex items-center justify-center">
                                <Check className="h-5 w-5 text-cream" strokeWidth={3} />
                            </div>
                            <p className="mono-label text-terracotta">[Confirmed] You're in.</p>
                        </div>

                        <h1 className="font-serif text-5xl md:text-7xl text-forest leading-[0.95] tracking-tight mb-8">
                            Welcome to <em className="italic">the channel</em>.
                        </h1>

                        <p className="text-lg text-muted_text leading-relaxed mb-10 max-w-xl">
                            We've received your details and added you to the Verde Investing WhatsApp broadcast. The next memo will land directly in your phone — typically 1–2 deals per quarter, never spam.
                        </p>

                        <div className="space-y-4 mb-12 max-w-md">
                            <Step n="01" title="You're on the list" body="No further action needed." done />
                            <Step n="02" title="We verify investor status" body="Pieter will reach out on WhatsApp shortly to confirm." />
                            <Step n="03" title="First deal memo arrives" body="The moment the next SPV opens, you'll be the first to see it." />
                        </div>

                        <Link
                            to="/"
                            data-testid="thank-you-return"
                            className="inline-flex items-center gap-3 mono-label text-forest border-b border-forest pb-1 hover:text-terracotta hover:border-terracotta transition-colors"
                        >
                            Return to the site
                            <ArrowUpRight className="h-3.5 w-3.5" />
                        </Link>
                    </div>

                    {/* Right: while-you-wait */}
                    <aside className="md:col-span-5">
                        <div className="bg-surface border border-hairline p-8 md:p-10">
                            <p className="mono-label text-terracotta mb-4">While you wait</p>
                            <h2 className="font-serif text-3xl text-forest leading-tight mb-6">
                                See the plots Pieter is scouting.
                            </h2>
                            <p className="text-sm text-muted_text leading-relaxed mb-8">
                                The deals you'll be invited into start as Instagram reels — raw, hand-shot, with the plot in the background. Follow along.
                            </p>

                            <a
                                href={INSTAGRAM_URL}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="thank-you-instagram"
                                className="group flex items-center gap-4 bg-forest text-cream p-5 mb-4 hover:bg-forest-hover transition-colors"
                            >
                                <Instagram className="h-5 w-5 shrink-0" />
                                <div className="flex-1">
                                    <p className="mono-label text-cream/60 mb-0.5">Instagram</p>
                                    <p className="font-serif text-xl leading-tight">@pietervanherpen</p>
                                </div>
                                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                            </a>

                            <a
                                href="https://www.verde2050.com"
                                target="_blank"
                                rel="noreferrer"
                                data-testid="thank-you-verde2050"
                                className="group flex items-center gap-4 border border-hairline p-5 hover:border-forest transition-colors"
                            >
                                <div className="w-10 h-10 rounded-full bg-forest/10 flex items-center justify-center shrink-0">
                                    <span className="font-serif text-forest text-lg">V</span>
                                </div>
                                <div className="flex-1">
                                    <p className="mono-label text-muted_text mb-0.5">Parent firm</p>
                                    <p className="font-serif text-xl text-forest leading-tight">Verde 2050</p>
                                </div>
                                <ArrowUpRight className="h-4 w-4 text-muted_text group-hover:text-forest transition-colors" />
                            </a>

                            <div className="mt-8 flex items-center gap-3 p-4 bg-cream border border-hairline">
                                <div className="w-9 h-9 rounded-full bg-[#25D366] flex items-center justify-center shrink-0">
                                    <WhatsAppIcon className="h-5 w-5 text-white" />
                                </div>
                                <div>
                                    <p className="mono-label text-muted_text">Channel</p>
                                    <p className="text-sm text-forest font-medium">Memos arrive here</p>
                                </div>
                            </div>
                        </div>
                    </aside>
                </div>
            </main>

            <footer className="border-t border-hairline py-8">
                <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mono-label text-muted_text">
                    <p>© {new Date().getFullYear()} Verde Investing — A Verde 2050 Company</p>
                    <div className="flex items-center gap-6">
                        <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer" className="hover:text-forest transition-colors flex items-center gap-2">
                            <Instagram className="h-3.5 w-3.5" /> Instagram
                        </a>
                        <a href="https://www.verde2050.com" target="_blank" rel="noreferrer" className="hover:text-forest transition-colors">
                            Verde 2050 ↗
                        </a>
                    </div>
                </div>
            </footer>
        </div>
    );
}

function Step({ n, title, body, done }) {
    return (
        <div className="flex gap-4">
            <div className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center mt-0.5 ${done ? "bg-forest text-cream" : "border border-hairline text-muted_text"}`}>
                {done ? <Check className="h-4 w-4" strokeWidth={3} /> : <span className="mono-label">{n}</span>}
            </div>
            <div>
                <p className="font-serif text-xl text-forest leading-tight">{title}</p>
                <p className="text-sm text-muted_text mt-1">{body}</p>
            </div>
        </div>
    );
}
